import PrintWrapper from './PrintWrapper';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import RouteTable from './RouteTable';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import { TLStops, TLShipments } from '@/config/types/Routes';
import { setActiveCols } from '@/actions/common/commonPure';
import { RootState } from '@/index';
import makeStyles from '@mui/styles/makeStyles';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Switch, Typography } from '@mui/material';
import i18next from 'i18next';
import { useSettings } from '../Settings/SettingsContext';
import Logo from '../Common/Logo/Logo';
import distanceIcon from '../Common/Icons/distance_grey.svg';
import { AccessTime, EditOutlined } from '@mui/icons-material';

const useStyles = makeStyles(theme => ({
  dimenionLabel: {
    left: '-4px'
  },
  logoSection: {
    paddingLeft: '0px',
    paddingTop: '5px'
  },
  gridHeader: {
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'space-between'
  },
  Pagebreakclass: {
    display: 'block',
    marginTop: '20px',
    padding: '0px 10px'
  },
  tourInfo: {
    marginBottom: '15px',
    background: '#ececec'
  },
  tourInfotext: {
    width: '100%',
    marginLeft: 0,
    padding: '0px'
  },
  infoBlock: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '16px',
    paddingTop: '10px !important',
    paddingBottom: '10px'
  },
  label: {
    fontWeight: 'bold',
    fontSize: '14px'
  },
  value: {
    marginLeft: '8px',
    fontSize: '14px'
  },
  distanceIcons: {
    width: '27px',
    height: '27px',
    marginRight: '8px',
  },
  Touricons: {
    marginRight: '8px',
    fontSize: '23px',
    color: '#757575'
  },
}));

type Dimension = {
  dimension: string;
}

interface Route {
  id: string;
  contactcompany: string;
  city: string;
  address: string;
  eta: string;
  load: string;
  duration: string;
  custom_tour: string;
  shipment_type: string;
  custom_id: string;
  vehicle: string;
  vehicle_name: string;
  start_time: string;
  end_time: string;
  distance: number;
  gross_duration: number;
  deliveries: any[];
  return_time: boolean;
  roundtrip: boolean;
  stops: TLStops[];
  shipments: TLShipments[];
}

interface ExportProps {
  routes: Route[];
  interactiveRollkarte?: boolean;
}

const defaultCols = ['id', 'contactcompany', 'city', 'address', 'eta', 'load', 'duration', 'custom_tour', 'shipment_type'];

/**
 * Clears all time-based and distance fields from stops and the route header,
 * since after a drag-and-drop move these values would be incorrect.
 */
function clearRouteTimingFields(route: Route): Route {
  return {
    ...route,
    start_time: null as any,
    end_time: null as any,
    distance: null as any,
    gross_duration: null as any,
    return_time: null as any,
    stops: route.stops.map(stop => ({
      ...stop,
      arrival_time: null as any,
      departure_time: null as any,
      preceding_driving_duration_s: null as any,
      preceding_idle_duration_s: null as any,
      preceding_driving_distance_m: null as any,
    })),
  };
}

const Export: React.FC<ExportProps> = ({ routes, interactiveRollkarte = false }) => {
  const dispatch = useDispatch();
  const activeCols = useSelector((state: RootState) => state.core.activeCols);
  const { selected_dimension } = useSelector((state: any) => state.core.loadunits, isEqual);
  const [selectedDimension, onSetDimension] = useState(0);
  const [dimension, setDimension] = useState<string>(selected_dimension);
  const { settings } = useSettings();
  const dimensions = settings.load_dimensions || [];
  const classes = useStyles();

  // Interactive Rollkarte state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableRoutes, setEditableRoutes] = useState<Route[]>([]);

  // Deep clone routes when entering edit mode, reset when leaving
  useEffect(() => {
    if (isEditMode) {
      setEditableRoutes(JSON.parse(JSON.stringify(routes)));
    } else {
      setEditableRoutes([]);
    }
  }, [isEditMode]);

  // Reset edit mode when the dialog routes change (e.g. different route set opened)
  useEffect(() => {
    setIsEditMode(false);
  }, [routes]);

  /**
   * Moves a shipment from one route to another (or reorders within the same route).
   * After the move, clears all timing/distance fields on affected routes.
   */
  const onMoveShipment = useCallback((
    sourceRouteId: string,
    shipmentCustomId: string,
    sourceStopIndex: number,
    targetRouteId: string,
    targetStopIndex: number
  ) => {
    setEditableRoutes(prev => {
      const newRoutes = JSON.parse(JSON.stringify(prev)) as Route[];
      const sourceRoute = newRoutes.find(r => String(r.id) === String(sourceRouteId));
      const targetRoute = newRoutes.find(r => String(r.id) === String(targetRouteId));
      if (!sourceRoute || !targetRoute) return prev;

      // Find the stop and shipment in the source route
      const srcStop = sourceRoute.stops[sourceStopIndex];
      if (!srcStop) return prev;
      const shipment = sourceRoute.shipments.find(s => s.custom_id === shipmentCustomId);
      if (!shipment) return prev;

      // Remove shipment from source stop's load/unload lists
      srcStop.shipments_to_load = (srcStop.shipments_to_load || []).filter(id => id !== shipmentCustomId);
      srcStop.shipments_to_unload = (srcStop.shipments_to_unload || []).filter(id => id !== shipmentCustomId);

      // Remove the stop if it has no more shipments
      const srcStopHasShipments =
        (srcStop.shipments_to_load?.length > 0) || (srcStop.shipments_to_unload?.length > 0);
      if (!srcStopHasShipments && srcStop.type !== 'depot') {
        sourceRoute.stops.splice(sourceStopIndex, 1);
      }

      // Remove shipment from source route's shipments array
      sourceRoute.shipments = sourceRoute.shipments.filter(s => s.custom_id !== shipmentCustomId);

      // Determine target stop index (clamped)
      const nonDepotTargetStops = targetRoute.stops
        .map((s, i) => ({ stop: s, idx: i }))
        .filter(s => s.stop.type !== 'depot');
      const clampedIdx = Math.min(targetStopIndex, nonDepotTargetStops.length);

      // Figure out the actual insertion index in the full stops array
      let insertIdx: number;
      if (clampedIdx >= nonDepotTargetStops.length) {
        // Insert after the last non-depot stop
        const lastNonDepot = nonDepotTargetStops[nonDepotTargetStops.length - 1];
        insertIdx = lastNonDepot ? lastNonDepot.idx + 1 : targetRoute.stops.length;
      } else {
        insertIdx = nonDepotTargetStops[clampedIdx].idx;
      }

      // Create a new stop for this shipment in the target route
      const newStop: TLStops = {
        type: 'customer',
        location: shipment.location || srcStop.location,
        address: srcStop.address,
        coordinates: srcStop.coordinates,
        preceding_driving_duration_s: null,
        preceding_idle_duration_s: null,
        preceding_driving_distance_m: null,
        arrival_time: null,
        departure_time: null,
        shipments_to_unload: shipment.type === 'delivery' ? [shipmentCustomId] : [],
        shipments_to_load: shipment.type === 'pickup' ? [shipmentCustomId] : [],
      };

      targetRoute.stops.splice(insertIdx, 0, newStop);

      // Add shipment to target route's shipments array
      targetRoute.shipments.push(shipment);

      // Clear timing/distance on affected routes
      const sourceIdx = newRoutes.findIndex(r => String(r.id) === String(sourceRouteId));
      const targetIdx = newRoutes.findIndex(r => String(r.id) === String(targetRouteId));
      if (sourceIdx >= 0) newRoutes[sourceIdx] = clearRouteTimingFields(newRoutes[sourceIdx]);
      if (targetIdx >= 0 && targetIdx !== sourceIdx) {
        newRoutes[targetIdx] = clearRouteTimingFields(newRoutes[targetIdx]);
      }

      // Remove routes that have no non-depot stops left
      return newRoutes.filter(r =>
        r.stops.some(s => s.type !== 'depot') || r.shipments.length > 0
      );
    });
  }, []);

  const displayRoutes = isEditMode && editableRoutes.length > 0 ? editableRoutes : routes;

  useEffect(() => {
    const backendIndex =
      dimensions[selectedDimension]?.backendIndex ?? selectedDimension;
    setDimension(String(backendIndex));
  }, [dimensions, selectedDimension]);

  const totalDistance = displayRoutes.reduce((acc, route) => {
    const sumOfDistance = route.stops?.reduce((sum, stop) => sum + (stop.preceding_driving_distance_m || 0), 0) || 0;
    return acc + sumOfDistance;
  }, 0);

  const totalDurationSeconds = displayRoutes.reduce((acc, route) => {
    const actualStartTime = (() => {
      const firstStop = route.stops?.[0];
      if (firstStop?.departure_time) {
        return firstStop.departure_time;
      }
      return route.start_time;
    })();

    const actualEndTime = (() => {
      const lastStop = route.stops?.[route.stops?.length - 1];
      if (lastStop?.arrival_time) {
        return lastStop.arrival_time;
      }
      return route.end_time;
    })();

    if (!actualStartTime || !actualEndTime) return acc;
    const startDate = new Date(actualStartTime);
    const endDate = new Date(actualEndTime);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return acc;
    
    const diff = (endDate.getTime() - startDate.getTime()) / 1000;
    return acc + (diff > 0 ? diff : 0);
  }, 0);

  const formatTimeHHMM = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return '00:00';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Toggle element for interactive mode — only shown when feature flag is enabled
  const interactiveToggle = interactiveRollkarte ? (
    <Button
      onClick={() => setIsEditMode(!isEditMode)}
      variant={isEditMode ? "contained" : "outlined"}
      style={{
        position: 'absolute',
        top: '13px',
        right: '180px',
        height: '40px',
        border: `1px solid ${isEditMode ? '#ef5927' : '#9e9e9e'}`,
        padding: '6px 12px',
        background: isEditMode ? '#ef5927' : 'transparent',
        boxShadow: 'none',
        textTransform: 'none',
      }}
      data-testid="interactive-rollkarte-toggle"
    >
      <EditOutlined style={{ color: isEditMode ? '#fff' : '#9e9e9e', marginRight: '6px' }} />
      <span style={{
        color: isEditMode ? '#fff' : '#666',
        fontSize: '14px',
        fontWeight: isEditMode ? 600 : 400,
      }}>
        {i18next.t('common:export.interactiveMode', { defaultValue: 'Interaktiv' })}
      </span>
    </Button>
  ) : null;

  return (
    <PrintWrapper setCols={(cols) => dispatch(setActiveCols(cols))}
      activeCols={activeCols} setDimension={setDimension} data-testid="export-print-wrapper"
      interactiveToggle={interactiveToggle}
    >
      <div className={classes.Pagebreakclass}>
        <Grid container spacing={2} alignItems="center" className={classes.gridHeader}>
          {/* Logo Section */}
          <Grid item xs={6} className={classes.logoSection}>
            <Logo w="260" h="50" />
          </Grid>
        </Grid>
      </div>

      {displayRoutes.map((route, i) => (
        <div 
          key={route.id} 
          style={{ 
            breakAfter: i < displayRoutes.length - 1 ? 'page' : 'auto',
            pageBreakAfter: i < displayRoutes.length - 1 ? 'always' : 'auto'
          }}
        >
          <RouteTable
            data-testid={`route-table-${route.id}`}
            dimension={dimension}
            activeCols={activeCols}
            route={route}
            setDimension={setDimension}
            logo={i === 0}
            isInteractive={isEditMode}
            routeId={String(route.id)}
            onDropShipment={onMoveShipment}
          />
        </div>
      ))}

      {(totalDistance > 0 || totalDurationSeconds > 0) && (
        <div className={`${classes.Pagebreakclass} print-page`}>
          <Grid container spacing={2} alignItems="center" className={`${classes.gridHeader} ${classes.tourInfo} ${classes.tourInfotext}`} wrap="nowrap" style={{ paddingLeft: '10px' }}>
            <Grid item className={classes.infoBlock}>
              <img src={distanceIcon} className={classes.distanceIcons} alt="distance" />
              <Typography variant="body1" className={classes.label}>
                {i18next.t('common:planning.totalDistance')}:
              </Typography>
              <span className={classes.value}>
                {Math.round(totalDistance / 1000 * 100) / 100} km
              </span>
            </Grid>
            <Grid item className={classes.infoBlock}>
              <AccessTime className={classes.Touricons} />
              <Typography variant="body1" className={classes.label}>
                {i18next.t('common:export.duration')}:
              </Typography>
              <span className={classes.value}>
                {formatTimeHHMM(totalDurationSeconds)}
              </span>
            </Grid>
          </Grid>
        </div>
      )}
    </PrintWrapper>
  );
}

export default Export;

